import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import { SubscriptionAccessStatus } from '@prisma/client';
import { Breadcrumb, Button, Input, Layout, Table } from 'antd';

import { SubscriptionAccessWithChildren } from '@app/domain-subscription-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { EligibilityRenderer } from '../../../../bundles';
import { AuditFilters, CreatedByColumn, UpdatedByColumn, UserPopover } from '../../../../common';
import { SubscriptionAccessStatusRenderer } from '../atoms';
import { useFindSubscriptionAccess } from '../data';

export const LandingPage = () => {
  const {
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<SubscriptionAccessWithChildren>(
    'user_accesses',
    [
      {
        title: 'Bundle',
        dataIndex: ['subscription', 'bundle', 'name'],
        width: 350,
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'User',
        dataIndex: ['subscription', 'user', 'name'],
        width: 300,
        ellipsis: true,
        sorter: true,
        render: (name: string, record: SubscriptionAccessWithChildren) => (
          <UserPopover user={record.subscription.user}>
            <Button type="link" size="small" style={{ padding: 0 }}>
              {name}
            </Button>
          </UserPopover>
        ),
      },
      {
        title: 'Access',
        dataIndex: ['bundleAccess', 'access', 'name'],
        width: 350,
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Eligible',
        width: 110,
        dataIndex: 'isEligible',
        sorter: true,
        render: (isEligible, record) => (
          <EligibilityRenderer isEligible={isEligible} eligibility={record.eligibility} />
        ),
        filters: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        filterMultiple: false,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 170,
        sorter: true,
        render: (status: SubscriptionAccessStatus, record) => (
          <SubscriptionAccessStatusRenderer
            access={record.bundleAccess.access}
            status={status}
            user={record.subscription.user}
          />
        ),
        filters: [
          { text: 'Queued', value: SubscriptionAccessStatus.QUEUED },
          { text: 'Queued', value: SubscriptionAccessStatus.RETRY },
          { text: 'Pending', value: SubscriptionAccessStatus.PENDING },
          { text: 'Completed', value: SubscriptionAccessStatus.COMPLETED },
          { text: 'Error', value: SubscriptionAccessStatus.ERROR },
        ],
      },
      {
        title: 'Status Description',
        dataIndex: 'statusDescription',
        width: 350,
      },
      { ...UpdatedByColumn, responsive: ['xxl'] },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 200,
        ellipsis: true,
        responsive: ['xxl'],
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      { ...CreatedByColumn, responsive: ['xxl'] },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 200,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ],
    {
      customFilters: AuditFilters,
      globalSearchFields: [
        'bundleAccess.access.name',
        'subscription.bundle.name',
        'subscription.user.name',
        'subscription.user.enterpriseId',
        'subscription.user.idsid',
        'subscription.user.email',
      ],
    },
  );

  const subscriptionAccessQuery = useFindSubscriptionAccess(getQuery({ where: { status: undefined } }));

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: 'Admin' },
          { title: 'Reports' },
          { title: 'Access Status' },
        ]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Access Status" />
        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} placeholder="Search..." />,
              <Button {...getReloadButtonProps()} onClick={() => subscriptionAccessQuery?.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(subscriptionAccessQuery?.data?.data)} />,
            ],
          })}
        />

        <Table {...getTableProps({ ...subscriptionAccessQuery })} />
      </Layout.Content>
    </>
  );
};
